import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { CardBrowser, type CardBrowserFilters } from "../components/CardBrowser";
import { Inspector } from "../components/Inspector";
import { pixelTheme } from "../theme/pixel";
import { defaultBtn } from "../theme/styles";
import { inertAttrs, inertOnClick } from "../logic/inert";
import type { CatalogCard } from "../api/client";
import { useActiveGameRedirect } from "../logic/useActiveGameRedirect";
import { useMe } from "../api/useMe";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";

const MARK_SEEN_DEBOUNCE_MS = 1000;
export const SEARCH_DEBOUNCE_MS = 300;

const DEFAULT_BROWSER_FILTERS: CardBrowserFilters = {
  q: "",
  // Default visible chips track the engine's full set; the
  // CardBrowser narrows them per-page via `availableFactions`. Keep
  // alphabetical so the URL-sync codec round-trips the same string.
  factions: ["dwarves", "elves", "goblins", "humans", "orcs"],
  type: "all",
};

export interface CollectionQuery {
  page: number;
  filters: CardBrowserFilters;
}

/**
 * Page + filters as one atomic query object, so the data effect fires
 * exactly once per user action:
 *
 * - Search keystrokes are debounced (~300 ms) — one fetch per pause,
 *   not one per keystroke. Chip toggles (faction / type) apply
 *   instantly, carrying whatever search text is current.
 * - Any filter change resets to page 1 in the same state update, so
 *   changing a filter while on page 3 can't fire a doomed page-3
 *   fetch first.
 *
 * Exported for tests.
 */
export function useCollectionQuery(debounceMs: number = SEARCH_DEBOUNCE_MS): {
  query: CollectionQuery;
  onFiltersChange: (next: CardBrowserFilters) => void;
  onPage: (page: number) => void;
} {
  const [query, setQuery] = useState<CollectionQuery>({
    page: 1,
    filters: DEFAULT_BROWSER_FILTERS,
  });
  // Latest applied query, readable from the stable callback below.
  const queryRef = useRef(query);
  queryRef.current = query;
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const onFiltersChange = useCallback(
    (next: CardBrowserFilters) => {
      const cur = queryRef.current.filters;
      const sameQ = cur.q === next.q;
      const sameRest =
        cur.type === next.type &&
        cur.factions.join(",") === next.factions.join(",");
      if (sameQ && sameRest) return;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (sameRest) {
        // Only the search text changed — debounce.
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          setQuery({ page: 1, filters: next });
        }, debounceMs);
      } else {
        setQuery({ page: 1, filters: next });
      }
    },
    [debounceMs],
  );

  const onPage = useCallback((page: number) => {
    setQuery((prev) => (prev.page === page ? prev : { ...prev, page }));
  }, []);

  return { query, onFiltersChange, onPage };
}

export function CollectionPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const { narrow } = useResponsiveLayout();
  useActiveGameRedirect();
  const [catalog, setCatalog] = useState<CatalogCard[]>([]);
  const [collection, setCollection] = useState<api.CollectionEntry[]>([]);
  const [owned, setOwned] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  // Once the first fetch resolves we never want the page to fall back
  // to the full "Loading…" placeholder again — re-filtering on a query
  // with no matches would otherwise unmount CardBrowser on every
  // keystroke (empty catalog + setLoading(true)) and steal focus.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inspect, setInspect] = useState<CatalogCard | null>(null);
  // From the shared /api/me cache — RequireAuth already probed it on
  // route change, so no extra request is issued here.
  const user = useMe();
  const [seenLocally, setSeenLocally] = useState<Set<string>>(new Set());
  const [totalCards, setTotalCards] = useState(0);
  const { query, onFiltersChange, onPage } = useCollectionQuery();
  const flushQueueRef = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<number | null>(null);

  const newCardIds = useMemo(() => {
    const s = new Set<string>();
    for (const e of collection) {
      if (e.viewed_at === null && !seenLocally.has(e.card_id)) {
        s.add(e.card_id);
      }
    }
    return s;
  }, [collection, seenLocally]);

  function flushSeen() {
    const ids = Array.from(flushQueueRef.current);
    flushQueueRef.current = new Set();
    flushTimerRef.current = null;
    if (ids.length === 0) return;
    void api.markCollectionSeen(ids).catch(() => {
      // Network failures: the next reload will surface unread rows again.
    });
  }

  function onCardSeen(cardId: string) {
    setSeenLocally((prev) => {
      if (prev.has(cardId)) return prev;
      const next = new Set(prev);
      next.add(cardId);
      return next;
    });
    flushQueueRef.current.add(cardId);
    if (flushTimerRef.current !== null) {
      window.clearTimeout(flushTimerRef.current);
    }
    flushTimerRef.current = window.setTimeout(
      flushSeen,
      MARK_SEEN_DEBOUNCE_MS,
    );
  }

  useEffect(() => {
    return () => {
      if (flushTimerRef.current !== null) {
        window.clearTimeout(flushTimerRef.current);
        flushSeen();
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([
      api.getOwnedCatalogPage(query.page, 50, query.filters),
      api.getCollectionPage(query.page, 50, query.filters),
    ])
      .then(([c, col]) => {
        if (cancelled) return;
        setCatalog(c.cards);
        setCollection(col.entries);
        setTotalCards(c.total);
        const nextOwned: Record<string, number> = {};
        for (const e of col.entries) nextOwned[e.card_id] = e.count;
        setOwned(nextOwned);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  if (error && error.includes("not_logged_in")) {
    navigate("/login", { replace: true });
    return null;
  }

  const totalOwned = Object.values(owned).reduce((a, b) => a + b, 0);

  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <AppHeader
        user={user}
        onBack={() => {
          if (window.history.length > 1) navigate(-1);
          else navigate("/lobby");
        }}
      />
      <Breadcrumb text="COLLECTION" />
      {!narrow && (
        <div style={{ color: theme.inkFaint, fontSize: 14 }}>
          {totalOwned} copies across {Object.keys(owned).length} unique cards
        </div>
      )}

      {error && (
        <div
          style={{
            padding: 12,
            background: theme.panel,
            border: `2px solid ${theme.accent}`,
            color: theme.accent,
          }}
        >
          {error}
        </div>
      )}

      {!hasLoadedOnce ? (
        <div style={{ color: theme.inkFaint }}>Loading…</div>
      ) : (
        <>
          <CardBrowser
            cards={catalog}
            owned={owned}
            theme={theme}
            ownedOnly
            urlSync
            serverFiltered
            onFiltersChange={onFiltersChange}
            newCardIds={newCardIds}
            onCardSeen={onCardSeen}
            onInspect={setInspect}
            availableFactions={user?.unlocked_factions}
          />
        </>
      )}

      {!loading && (
        <PaginationControls
          page={query.page}
          perPage={50}
          total={totalCards}
          theme={theme}
          onPage={onPage}
        />
      )}

      <Inspector card={inspect} theme={theme} onClose={() => setInspect(null)} />
    </div>
  );
}

function PaginationControls({
  page,
  perPage,
  total,
  theme,
  onPage,
}: {
  page: number;
  perPage: number;
  total: number;
  theme: typeof pixelTheme;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
      <button
        type="button"
        onClick={inertOnClick(page <= 1, () => onPage(Math.max(1, page - 1)))}
        {...inertAttrs(page <= 1)}
        {...defaultBtn(theme, page <= 1)}
      >
        PREV
      </button>
      <span style={{ color: theme.inkFaint, fontSize: 12 }}>
        Page {page} / {pages} · {total} cards
      </span>
      <button
        type="button"
        onClick={inertOnClick(page >= pages, () =>
          onPage(Math.min(pages, page + 1)),
        )}
        {...inertAttrs(page >= pages)}
        {...defaultBtn(theme, page >= pages)}
      >
        NEXT
      </button>
    </div>
  );
}
